// PREMIOS INDIVIDUAIS da temporada.
//
// O MVP nao e o cestinha. Na vida real ele quase sempre sai de um time de
// cima da tabela -- producao com vitoria pesa mais que producao em time ruim,
// e e por isso que a formula aqui multiplica a producao pelo aproveitamento da
// equipe. Sem isso, o premio vira "quem mais arremessou no pior time da liga",
// que e o erro classico de simulador de basquete.
//
// Tudo aqui e calculado A PARTIR do agregado da temporada (`seasonStats`), uma
// vez, no fim. Nada disso roda por rodada.

import { derive } from './boxscore.js';

/** Produção por jogo, no formato que as formulas usam. */
function perGame(line) {
  const g = Math.max(1, line.games);
  return {
    line,
    games: line.games,
    pts: line.pts / g,
    reb: (line.orb + line.drb) / g,
    ast: line.ast / g,
    stl: line.stl / g,
    blk: line.blk / g,
    tov: line.tov / g,
    min: line.seconds / 60 / g,
    ts: derive(line, line.games).tsPct,
    tpm: line.tpm / g,
    fgm2: (line.fgm - line.tpm) / g,
  };
}

/** Quanto da temporada o atleta precisa ter jogado para concorrer. */
function elegiveis(seasonStats, totalRounds, fracao = 0.6) {
  const minGames = Math.max(5, Math.round(totalRounds * fracao));
  return Object.values(seasonStats)
    .filter((l) => l.games >= minGames && l.seconds > 0)
    .map(perGame);
}

/**
 * Premios da temporada. Devolve um objeto pronto para guardar no historico:
 * cada premio tem vencedor, numero que o justifica, e os finalistas.
 */
export function computeAwards({ seasonStats, standings, teams, totalRounds }) {
  const pool = elegiveis(seasonStats, totalRounds);
  if (!pool.length) return null;

  const winPctOf = (teamId) => {
    const row = standings[teamId];
    return row && row.games ? row.wins / row.games : 0.5;
  };
  const teamName = (teamId) => teams.find((t) => t.id === teamId)?.name || '';
  const playerById = (id) => {
    for (const t of teams) {
      const p = t.roster.find((x) => x.id === id);
      if (p) return p;
    }
    return null;
  };

  // Minutos por jogo dentro do proprio time: quem esta fora dos 5 primeiros
  // saiu do banco. E o criterio do premio de sexto homem.
  const rankNoTime = new Map();
  const porTime = new Map();
  for (const p of pool) {
    const arr = porTime.get(p.line.teamId) || [];
    arr.push(p);
    porTime.set(p.line.teamId, arr);
  }
  for (const arr of porTime.values()) {
    arr.sort((a, b) => b.min - a.min);
    arr.forEach((p, i) => rankNoTime.set(p.line.playerId, i));
  }

  const premio = (nome, descricao, lista, valorFn, unidade) => {
    if (!lista.length) return null;
    const finalistas = lista.slice(0, 5).map((p) => ({
      playerId: p.line.playerId,
      name: p.line.name,
      teamId: p.line.teamId,
      teamName: teamName(p.line.teamId),
      valor: Math.round(valorFn(p) * 10) / 10,
    }));
    return { nome, descricao, unidade, vencedor: finalistas[0], finalistas };
  };

  // ------------------------------------------------------------------ MVP
  const mvpScore = (p) => {
    const producao = p.pts + p.reb * 0.75 + p.ast * 0.85
      + p.stl * 1.6 + p.blk * 1.4 - p.tov * 0.8;
    const eficiencia = 1 + (p.ts - 0.55) * 0.9;      // arremessar bem conta
    const time = 0.45 + winPctOf(p.line.teamId) * 1.1; // vitoria pesa muito
    return producao * eficiencia * time;
  };
  const mvpLista = [...pool].sort((a, b) => mvpScore(b) - mvpScore(a));

  // ------------------------------------------------------------- cestinhas
  const cestinha = [...pool].sort((a, b) => b.pts - a.pts);
  const tresPontos = [...pool].sort((a, b) => b.tpm - a.tpm);
  const doisPontos = [...pool].sort((a, b) => b.fgm2 - a.fgm2);

  // -------------------------------------------------------------- defensor
  const defScore = (p) => {
    const atleta = playerById(p.line.playerId);
    const defesa = atleta ? (atleta.attributes?.defense ?? 75) : 75;
    return p.stl * 2.4 + p.blk * 2.2 + p.reb * 0.35 + (defesa - 75) * 0.16;
  };
  const defensor = [...pool].sort((a, b) => defScore(b) - defScore(a));

  // ---------------------------------------------------------------- novato
  const novatos = pool.filter((p) => playerById(p.line.playerId)?.rookie);
  const novatoLista = [...novatos].sort((a, b) => mvpScore(b) - mvpScore(a));

  // ---------------------------------------------------------- sexto homem
  const banco = pool.filter((p) => (rankNoTime.get(p.line.playerId) ?? 0) >= 5);
  const sextoLista = [...banco].sort((a, b) => b.pts - a.pts);

  return {
    mvp: premio('MVP', 'Melhor da temporada', mvpLista, (p) => p.pts, 'pts'),
    cestinha: premio('Cestinha', 'Mais pontos por jogo', cestinha, (p) => p.pts, 'pts'),
    tresPontos: premio('Rei do 3', 'Mais cestas de 3 por jogo', tresPontos, (p) => p.tpm, 'de 3'),
    doisPontos: premio('Rei do garrafao', 'Mais cestas de 2 por jogo', doisPontos, (p) => p.fgm2, 'de 2'),
    defensor: premio('Melhor defensor', 'Roubos, tocos e defesa', defensor,
      (p) => p.stl + p.blk, 'rb+tc'),
    novato: premio('Novato do ano', 'Melhor calouro', novatoLista, (p) => p.pts, 'pts'),
    sextoHomem: premio('Sexto homem', 'Melhor saindo do banco', sextoLista, (p) => p.pts, 'pts'),
  };
}

export const AWARD_ORDER = [
  'mvp', 'cestinha', 'tresPontos', 'doisPontos', 'defensor', 'novato', 'sextoHomem',
];
